import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  TrashIcon,
  LockClosedIcon,
  LockOpenIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  ChevronRightIcon,
  ArchiveBoxIcon,
  ExclamationCircleIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftEllipsisIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { interviewsAPI, cvAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_IDX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

// Store the Maps URL so candidate emails keep receiving the existing location value.
const INTERVIEW_LOCATIONS = [
  { name: 'UN Consortium', url: 'https://maps.app.goo.gl/6VxpCUXYw8tpBA7z8' },
  { name: 'PGC Education', url: 'https://maps.app.goo.gl/EhCLisE2wJ9oHZvg6' },
  { name: 'Park Hospitals', url: 'https://maps.app.goo.gl/7mD4yo5cyHvKo9ae6' },
  { name: 'Forte Diagnostics', url: 'https://maps.app.goo.gl/bcoxmv9mgv1oPMMp7' },
];

const TABS = [
  { id: 'slots', label: 'Slot Setup', icon: CalendarDaysIcon },
  { id: 'bookings', label: 'Scheduled Interviews', icon: UserGroupIcon },
  { id: 'archived', label: 'Archived', icon: ArchiveBoxIcon },
  { id: 'template', label: 'Email Template', icon: PaperAirplaneIcon },
];

export default function InterviewScheduling() {
  const { departmentId } = useParams();
  const deptId = parseInt(departmentId, 10);
  const [activeTab, setActiveTab] = useState('slots');
  const [slots, setSlots] = useState([]);
  const [archivedSlots, setArchivedSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [positions, setPositions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  // Lives on the PARENT, not inside SlotsTab, and that placement is the whole
  // point. reload() flips `loading`, which unmounts everything inside the
  // ternary below — so a notice held in SlotsTab would be destroyed by the very
  // refresh that makes the archived slot appear. Same trap CvScreening.jsx
  // documents (see the note in runScreen: "do NOT call reload() here"), which
  // solved it by deferring the refresh to modal close. There is no modal here,
  // so the message is lifted OUT of the unmounted subtree instead. Deferring
  // would have worked too, but then the Archived tab's count would not move
  // until later, and the whole point is that HR sees where the slot went.
  const [notice, setNotice] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, aRes, bRes, pRes] = await Promise.all([
        interviewsAPI.getSlots(deptId),
        interviewsAPI.getSlots(deptId, { archived: true }),
        interviewsAPI.getBookings(deptId, { include_cancelled: true }),
        cvAPI.listPositions(deptId),
      ]);
      setSlots(sRes.data);
      setArchivedSlots(aRes.data);
      setBookings(bRes.data);
      setPositions(pRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Interview Scheduling</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage interview slots and view candidate bookings.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === t.id
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
              {t.id === 'archived' && archivedSlots.length > 0 && (
                <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded-full text-[10px] font-semibold">
                  {archivedSlots.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Rendered OUTSIDE the loading ternary so it survives the refresh that
          follows it. A "Delete" that archived instead has to say so and say
          where the slot went; vanishing silently would be a quieter version of
          the bug this replaces. */}
      {notice && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <ArchiveBoxIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900">
            <p>{notice.message}</p>
            {notice.action === 'archived' && (
              <button
                onClick={() => { setActiveTab('archived'); setNotice(null); }}
                className="mt-2 text-sm font-medium text-amber-800 underline hover:text-amber-900"
              >
                View it in the Archived tab
              </button>
            )}
          </div>
          <button
            onClick={() => setNotice(null)}
            className="text-amber-500 hover:text-amber-700"
            title="Dismiss"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <>
          {activeTab === 'slots' && (
            <SlotsTab
              deptId={deptId}
              slots={slots}
              positions={positions}
              onReload={reload}
              onNotice={setNotice}
            />
          )}
          {activeTab === 'bookings' && (
            <BookingsTab bookings={bookings} slots={slots} onReload={reload} />
          )}
          {activeTab === 'archived' && (
            <ArchivedTab slots={archivedSlots} />
          )}
          {activeTab === 'template' && (
            <TemplateTab deptId={deptId} />
          )}
        </>
      )}
    </div>
  );
}

// ── Slot Setup Tab ────────────────────────────────────────────────────────────

function SlotsTab({ deptId, slots, positions, onReload, onNotice }) {
  const { canModule } = useAuth();
  const canAdd = canModule('hr_interview_scheduling', 'add');
  const [showBulk, setShowBulk] = useState(false);
  const [showSingle, setShowSingle] = useState(false);

  // Group slots by date for display
  const grouped = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      if (!map[s.slot_date]) map[s.slot_date] = [];
      map[s.slot_date].push(s);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  const handleToggleLock = async (slot) => {
    try {
      await interviewsAPI.updateSlot(slot.id, { locked: !slot.locked });
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to update slot');
    }
  };

  const handleDelete = async (slot) => {
    if (!window.confirm(`Delete slot on ${slot.slot_date} at ${fmtTime(slot.slot_time)}?`)) return;
    try {
      const res = await interviewsAPI.deleteSlot(slot.id);
      const data = res?.data || {};
      // Report what the server ACTUALLY did. A slot holding cancelled bookings
      // is archived rather than deleted, and if that just vanished from this
      // list HR would believe it was deleted -- the same lie, told quietly.
      // onNotice lives on the parent so the reload below cannot destroy it.
      if (data.action === 'archived') {
        onNotice({ action: 'archived', message: data.message });
      } else {
        onNotice(null);
      }
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete slot');
    }
  };

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => { setShowBulk(true); setShowSingle(false); }}
          disabled={!canAdd}
          title={canAdd ? '' : "🔒 You don't have permission"}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CalendarDaysIcon className="h-4 w-4" />
          Bulk Create Slots
        </button>
        <button
          onClick={() => { setShowSingle(true); setShowBulk(false); }}
          disabled={!canAdd}
          title={canAdd ? '' : "🔒 You don't have permission"}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="h-4 w-4" />
          Add Single Slot
        </button>
      </div>

      {/* Bulk creator.
          The refresh happens on CLOSE, not on create. reload() flips the page's
          `loading` flag, which unmounts this whole tab — and with it the panel
          reporting what the create just did. Closing first means nothing is
          unmounted while there is still something to read. */}
      {showBulk && (
        <BulkSlotCreator
          deptId={deptId}
          positions={positions}
          onClose={() => { setShowBulk(false); onReload(); }}
        />
      )}

      {/* Single slot creator */}
      {showSingle && (
        <SingleSlotCreator
          deptId={deptId}
          positions={positions}
          onCreated={() => { setShowSingle(false); onReload(); }}
          onCancel={() => setShowSingle(false)}
        />
      )}

      {/* Slot list */}
      {grouped.length === 0 ? (
        <EmptyState
          icon={CalendarDaysIcon}
          title="No interview slots"
          description="Use 'Bulk Create Slots' to generate slots across a date range."
        />
      ) : (
        <div className="space-y-4">
          {grouped.map(([date, daySlots]) => (
            <div key={date} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <CalendarDaysIcon className="h-4 w-4 text-gray-500" />
                <span className="font-semibold text-gray-700 text-sm">
                  {fmtDateFull(date)}
                </span>
                <span className="ml-auto text-xs text-gray-400">
                  {daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {daySlots.map((slot) => (
                  <SlotRow
                    key={slot.id}
                    slot={slot}
                    onToggleLock={() => handleToggleLock(slot)}
                    onDelete={() => handleDelete(slot)}
                    onReload={onReload}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SlotRow({ slot, onToggleLock, onDelete, onReload }) {
  const pct = slot.max_capacity > 0
    ? Math.round((slot.booking_count / slot.max_capacity) * 100)
    : 0;
  const full = slot.booking_count >= slot.max_capacity;

  return (
    <div className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
      <div className="flex items-center gap-2 w-20">
        <ClockIcon className="h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">{fmtTime(slot.slot_time)}</span>
      </div>

      {/* Capacity bar */}
      <div className="flex items-center gap-2 w-40">
        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              full ? 'bg-red-400' : pct >= 75 ? 'bg-amber-400' : 'bg-emerald-400'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {slot.booking_count}/{slot.max_capacity}
        </span>
      </div>

      {slot.location && (
        <span className="text-xs text-gray-500 truncate max-w-[160px]">{slot.location}</span>
      )}

      {slot.position_title && (
        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">
          {slot.position_title}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        {slot.locked ? (
          <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
            <LockClosedIcon className="h-3 w-3" /> Locked
          </span>
        ) : full ? (
          <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            Full
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            <LockOpenIcon className="h-3 w-3" /> Open
          </span>
        )}

        <button
          onClick={onToggleLock}
          title={slot.locked ? 'Unlock slot' : 'Lock slot'}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          {slot.locked ? <LockOpenIcon className="h-4 w-4" /> : <LockClosedIcon className="h-4 w-4" />}
        </button>
        <button
          onClick={onDelete}
          title="Delete slot"
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ── Bulk slot creator form ────────────────────────────────────────────────────

function BulkSlotCreator({ deptId, positions, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date_from: today,
    date_to: '',
    days: [0, 1, 2, 3, 4], // Mon-Fri default
    times: ['09:00', '10:00', '11:00', '14:00', '15:00'],
    max_capacity: 1,
    location: '',
    notes: '',
    position_id: '',
  });
  const [newTime, setNewTime] = useState('');
  const [saving, setSaving] = useState(false);
  // The outcome of the last run, rendered inline. It replaces an alert(), which
  // was wrong twice over: alert() blocks the event loop, so `saving` stayed true
  // and the button read "Creating..." for as long as the dialog was up; and the
  // result now has structure — two counts, a reason, a date list — that a
  // single-line dialog renders badly.
  const [result, setResult] = useState(null);

  const toggleDay = (idx) => {
    setForm((f) => ({
      ...f,
      days: f.days.includes(idx) ? f.days.filter((d) => d !== idx) : [...f.days, idx].sort(),
    }));
  };

  const addTime = () => {
    const t = newTime.trim();
    if (!t || form.times.includes(t)) return;
    setForm((f) => ({ ...f, times: [...f.times, t].sort() }));
    setNewTime('');
  };

  const removeTime = (t) => setForm((f) => ({ ...f, times: f.times.filter((x) => x !== t) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date_to) { alert('Please set an end date'); return; }
    if (form.days.length === 0) { alert('Select at least one day'); return; }
    if (form.times.length === 0) { alert('Add at least one time slot'); return; }
    setSaving(true);
    try {
      const res = await interviewsAPI.createSlotsBulk(deptId, {
        date_from: form.date_from,
        date_to: form.date_to,
        days_of_week: form.days,
        time_slots: form.times,
        max_capacity: parseInt(form.max_capacity, 10),
        location: form.location || null,
        notes: form.notes || null,
        position_id: form.position_id ? parseInt(form.position_id, 10) : null,
      });
      const { created = [], skipped = [] } = res.data || {};
      setResult({ created, skipped });
      // DO NOT call onReload() here. This is the same trap CvScreening.jsx
      // already documents at its runScreen handler, and the first version of
      // this panel walked straight into it:
      //
      //   reload() sets the page's `loading` flag, and the page renders
      //   {loading ? <spinner> : <SlotsTab/>}. Flipping it UNMOUNTS SlotsTab,
      //   which owns `showBulk`. On remount showBulk is back to its initial
      //   false, so this whole component — and the `result` just stored in it —
      //   is destroyed before it can paint.
      //
      // The slots were created and the user saw nothing at all. React 18 removed
      // the "setState on an unmounted component" warning, so the console was
      // clean too: silent in every direction.
      //
      // The list is refreshed when the panel closes instead — see onClose in
      // SlotsTab. Guarding the panel against being CLOSED (which the previous
      // comment here did) was guarding the wrong thing; it was never closed,
      // it was unmounted underneath.
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Failed to create slots' });
    } finally {
      setSaving(false);
    }
  };

  // "successfully" is never used for a zero result, and the skip count always
  // appears beside the created count so a partial run cannot read as a complete
  // one. Both sentences come from the server's own reason, not a fixed string.
  const summary = (r) => {
    if (!r || r.error) return null;
    const c = r.created.length;
    const s = r.skipped.length;
    const head = c === 0 ? 'Created 0 slots.' : `Created ${c} slot${c === 1 ? '' : 's'}.`;
    if (s === 0) return head;
    const dates = r.skipped
      .map((x) => new Date(x.slot_date + 'T00:00:00')
        .toLocaleDateString(undefined, { day: 'numeric', month: 'short' }))
      .join(', ');
    // Every skip in one run currently shares a reason; if that stops being
    // true the distinct details are listed rather than the first one winning.
    const reasons = Array.from(new Set(r.skipped.map((x) => x.detail)));
    const why = reasons.length === 1 ? reasons[0] : reasons.join('; ');
    return `${head} ${s} date${s === 1 ? ' was' : 's were'} skipped because ${why}: ${dates}.`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="font-semibold text-gray-800 mb-4">Bulk Create Interview Slots</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date range */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={form.date_from} min={today}
              onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={form.date_to} min={form.date_from || today}
              onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
        </div>

        {/* Days of week */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Days of week</label>
          <div className="flex gap-2">
            {DAYS.map((d, idx) => (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(idx)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  form.days.includes(idx)
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Time slots */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Time slots</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {form.times.map((t) => (
              <span key={t} className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs">
                {fmtTime(t)}
                <button type="button" onClick={() => removeTime(t)}
                  className="text-indigo-400 hover:text-indigo-700 ml-0.5">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            <button type="button" onClick={addTime}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Add
            </button>
          </div>
        </div>

        {/* Capacity + location */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max candidates per slot</label>
            <input type="number" min={1} value={form.max_capacity}
              onChange={(e) => setForm((f) => ({ ...f, max_capacity: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label htmlFor="bulk-slot-location" className="block text-xs font-medium text-gray-600 mb-1">Location (optional)</label>
            <select id="bulk-slot-location" value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option value="">Select a location (optional)</option>
              {INTERVIEW_LOCATIONS.map(({ name, url }) => (
                <option key={url} value={url}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Position filter */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Position (optional — leave blank for any)</label>
          <select value={form.position_id}
            onChange={(e) => setForm((f) => ({ ...f, position_id: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
            <option value="">Any position</option>
            {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
            {saving ? 'Creating...' : 'Create Slots'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
            {result ? 'Done' : 'Cancel'}
          </button>
        </div>

        {/* The outcome, inline. Rendered here rather than in an alert() so the
            counts, the reason and the date list have room, and so the panel
            stays open long enough to read them. */}
        {result?.error && (
          <div className="mt-2 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <XCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{result.error}</span>
          </div>
        )}
        {result && !result.error && (
          <div className={`mt-2 flex items-start gap-2 text-sm rounded-lg px-3 py-2 border ${
            result.created.length === 0
              ? 'text-amber-800 bg-amber-50 border-amber-200'
              : result.skipped.length > 0
                ? 'text-gray-700 bg-gray-50 border-gray-200'
                : 'text-green-800 bg-green-50 border-green-200'
          }`}>
            {result.created.length === 0
              ? <ExclamationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
              : <CheckCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div>{summary(result)}</div>
              {result.skipped.length > 0 && (
                <div className="mt-1 text-xs opacity-80">
                  Nothing was changed on those dates. Existing slots are left as
                  they are — delete one first if you want to replace it.
                </div>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

// ── Single slot creator ───────────────────────────────────────────────────────

function SingleSlotCreator({ deptId, positions, onCreated, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    slot_date: today,
    slot_time: '09:00',
    max_capacity: 1,
    location: '',
    position_id: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await interviewsAPI.createSlot(deptId, {
        slot_date: form.slot_date,
        slot_time: form.slot_time,
        max_capacity: parseInt(form.max_capacity, 10),
        location: form.location || null,
        position_id: form.position_id ? parseInt(form.position_id, 10) : null,
      });
      onCreated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create slot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="font-semibold text-gray-800 mb-4">Add Single Slot</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" value={form.slot_date} min={today}
              onChange={(e) => setForm((f) => ({ ...f, slot_date: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
            <input type="time" value={form.slot_time}
              onChange={(e) => setForm((f) => ({ ...f, slot_time: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max candidates</label>
            <input type="number" min={1} value={form.max_capacity}
              onChange={(e) => setForm((f) => ({ ...f, max_capacity: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
          </div>
          <div>
            <label htmlFor="single-slot-location" className="block text-xs font-medium text-gray-600 mb-1">Location (optional)</label>
            <select id="single-slot-location" value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
              <option value="">Select a location (optional)</option>
              {INTERVIEW_LOCATIONS.map(({ name, url }) => (
                <option key={url} value={url}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Position</label>
          <select value={form.position_id}
            onChange={(e) => setForm((f) => ({ ...f, position_id: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
            <option value="">Any position</option>
            {positions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <button type="submit" disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
            {saving ? 'Adding...' : 'Add Slot'}
          </button>
          <button type="button" onClick={onCancel}
            className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Bookings Tab ──────────────────────────────────────────────────────────────

function BookingsTab({ bookings, slots, onReload }) {
  const [showCancelled, setShowCancelled] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleSlotId, setRescheduleSlotId] = useState('');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  // The booking whose attendance is being confirmed, or null. Holds the ROW,
  // not just an id, so the modal can name the candidate without re-searching a
  // list that is about to be refreshed underneath it.
  const [attendanceFor, setAttendanceFor] = useState(null);

  const displayed = showCancelled ? bookings : bookings.filter((b) => !b.cancelled);

  // "Today" in Asia/Colombo as YYYY-MM-DD (slot_date is an ISO date string, so
  // lexicographic comparison against this is correct). en-CA formats as ISO.
  const todayStr = useMemo(
    () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Colombo' }).format(new Date()),
    [],
  );

  // Split into upcoming (>= today, or no date) and past (< today). Bookings with
  // no slot_date stay visible in Upcoming rather than hidden in collapsed Past.
  const sortKey = (b) => `${b.slot_date || ''} ${b.slot_time || ''}`;
  const upcoming = displayed
    .filter((b) => !b.slot_date || b.slot_date >= todayStr)
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));   // soonest first
  const past = displayed
    .filter((b) => b.slot_date && b.slot_date < todayStr)
    .sort((a, b) => sortKey(b).localeCompare(sortKey(a)));   // most recent first

  const handleCancel = async (b) => {
    const reason = window.prompt('Reason for cancellation (optional):');
    if (reason === null) return; // dismissed
    try {
      await interviewsAPI.cancelBooking(b.id, { reason: reason || null });
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleSlotId) { alert('Select a new slot'); return; }
    setRescheduling(true);
    try {
      await interviewsAPI.rescheduleBooking(rescheduleId, {
        new_slot_id: parseInt(rescheduleSlotId, 10),
        note: rescheduleNote || null,
      });
      setRescheduleId(null);
      onReload();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  const availableSlots = slots.filter((s) => s.is_available);

  const renderTable = (rows, { muted = false } = {}) => (
    <div className={`bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm ${muted ? 'opacity-70' : ''}`}>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Candidate</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Position</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Date & Time</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Location</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
            <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((b) => (
            <tr key={b.id} className={`hover:bg-gray-50 ${b.cancelled ? 'opacity-50' : ''}`}>
              <td className="px-4 py-3">
                <div className="font-medium text-gray-800">{b.candidate_name}</div>
                <div className="text-xs text-gray-400">{b.candidate_email}</div>
              </td>
              <td className="px-4 py-3 text-gray-600">{b.position_title || '—'}</td>
              <td className="px-4 py-3">
                <div className="text-gray-800 flex items-center gap-2">
                  {b.slot_date ? fmtDateShort(b.slot_date) : '—'}
                  {b.slot_date === todayStr && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                      Today
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">{b.slot_time ? fmtTime(b.slot_time) : ''}</div>
              </td>
              <td className="px-4 py-3 text-gray-600">{b.location || '—'}</td>
              <td className="px-4 py-3">
                {/* ORDER IS THE LOGIC. Cancelled outranks everything — a
                    cancelled interview has no attendance to report. A recorded
                    attendance outranks "Confirmed", because once we know
                    whether they turned up, "Confirmed" is the stalest true
                    thing we could say. Only then does the amber prompt stand in
                    for "Confirmed", so a past interview reads as an outstanding
                    task rather than as settled. */}
                {b.cancelled ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 rounded-full text-xs">
                    <XCircleIcon className="h-3.5 w-3.5" /> Cancelled
                  </span>
                ) : b.attendance_status ? (
                  <AttendanceBadge booking={b} />
                ) : b.needs_attendance_confirmation ? (
                  <span
                    title="This interview has passed — confirm whether the candidate attended."
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs"
                  >
                    <ExclamationCircleIcon className="h-3.5 w-3.5" /> Pending Confirmation
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs">
                    <CheckCircleIcon className="h-3.5 w-3.5" /> Confirmed
                  </span>
                )}
              </td>
              <td className="px-4 py-3">
                {!b.cancelled && (
                  <div className="flex gap-2">
                    {/* Shown ONLY while the server says confirmation is
                        outstanding. The condition is the server's computed
                        field and not a date comparison repeated here: the
                        timezone argument behind it lives in one place
                        (routers/interviews.py::_needs_attendance_confirmation),
                        and a second copy in the browser's zone would disagree
                        with it for anyone not sitting in Asia/Colombo. */}
                    {b.needs_attendance_confirmation && (
                      <button
                        onClick={() => setAttendanceFor(b)}
                        title="Confirm attendance"
                        className="p-1.5 rounded-lg text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                      >
                        <ClipboardDocumentCheckIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => { setRescheduleId(b.id); setRescheduleSlotId(''); setRescheduleNote(''); }}
                      title="Reschedule"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleCancel(b)}
                      title="Cancel booking"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
                    >
                      <XCircleIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          Scheduled Interviews
          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
            {upcoming.length} upcoming
          </span>
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showCancelled}
            onChange={(e) => setShowCancelled(e.target.checked)}
            className="rounded text-primary-600" />
          Show cancelled
        </label>
      </div>

      {displayed.length === 0 ? (
        <EmptyState
          icon={UserGroupIcon}
          title="No bookings yet"
          description="Bookings appear here once candidates schedule their interviews."
        />
      ) : (
        <>
          {/* Upcoming */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">Upcoming</h3>
            {upcoming.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-400">
                No upcoming interviews.
              </div>
            ) : renderTable(upcoming)}
          </div>

          {/* Past — collapsed by default */}
          {past.length > 0 && (
            <div className="space-y-2">
              <button
                onClick={() => setShowPast((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-700"
              >
                <ChevronRightIcon className={`h-4 w-4 transition-transform ${showPast ? 'rotate-90' : ''}`} />
                Past Interviews ({past.length})
              </button>
              {showPast && renderTable(past, { muted: true })}
            </div>
          )}
        </>
      )}

      {/* Reschedule modal */}
      {rescheduleId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <h3 className="font-semibold text-gray-800 mb-4">Reschedule Interview</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">New slot</label>
                <select value={rescheduleSlotId}
                  onChange={(e) => setRescheduleSlotId(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                  <option value="">Select a slot…</option>
                  {availableSlots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {fmtDateShort(s.slot_date)} — {fmtTime(s.slot_time)}
                      {s.location ? ` (${s.location})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Note to candidate (optional)</label>
                <textarea rows={3} value={rescheduleNote}
                  onChange={(e) => setRescheduleNote(e.target.value)}
                  placeholder="Reason for rescheduling…"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleReschedule} disabled={rescheduling}
                className="flex-1 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                {rescheduling ? 'Rescheduling…' : 'Reschedule'}
              </button>
              <button onClick={() => setRescheduleId(null)}
                className="flex-1 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attendance modal.
          THE REFRESH HAPPENS ON CLOSE, NOT ON SUBMIT, and this is the third
          time this page shape has had to learn it - CvScreening.jsx documents
          it at runScreen, and the bulk slot panel shipped the bug in 4e2a871
          before 3a3fa24 fixed it the same way.

          reload() flips the PAGE's `loading` flag, and the page renders
          {loading ? <spinner> : <BookingsTab/>}. Flipping it UNMOUNTS
          BookingsTab, which owns `attendanceFor` - so the modal and anything it
          had just put on screen are destroyed mid-submit. React 18 removed the
          unmounted-setState warning, so it fails silently in every direction.

          Closing first means nothing is unmounted while there is still
          something to read, and the list is refreshed immediately after. */}
      {attendanceFor && (
        <AttendanceModal
          booking={attendanceFor}
          onClose={() => { setAttendanceFor(null); onReload(); }}
        />
      )}
    </div>
  );
}


// -- Attendance badge ---------------------------------------------------------
// A recorded outcome. The remark, when there is one, is reachable two ways:
// hovering (the native title) and clicking (the inline panel), because a title
// alone is invisible on touch and to anyone not hunting for it.

function AttendanceBadge({ booking: b }) {
  const [open, setOpen] = useState(false);
  const attended = b.attendance_status === 'attended';
  const remark = b.attendance_remark || null;

  const Icon = attended ? CheckCircleIcon : NoSymbolIcon;
  const label = attended ? 'Attended' : 'No Show';
  const tone = attended
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-gray-100 text-gray-600';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => remark && setOpen((v) => !v)}
        title={remark ? `Remark: ${remark}` : label}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${tone} ${
          remark ? 'cursor-pointer hover:brightness-95' : 'cursor-default'
        }`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
        {remark && <ChatBubbleLeftEllipsisIcon className="h-3 w-3 opacity-70" />}
      </button>
      {open && remark && (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-700 shadow-lg">
          {remark}
        </div>
      )}
    </div>
  );
}


// -- Attendance modal ---------------------------------------------------------

function AttendanceModal({ booking, onClose }) {
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Set once the PATCH has succeeded. The modal stays up showing it, and the
  // list is refreshed by onClose - see the note at the mount site.
  const [done, setDone] = useState(null);

  const submit = async (status) => {
    setSaving(true);
    setError(null);
    try {
      await interviewsAPI.markAttendance(booking.id, {
        status,
        remark: remark.trim() || null,
      });
      setDone(status);
      // DO NOT call onReload() here - there is deliberately no onReload prop on
      // this component to call. reload() flips the page's `loading` flag, which
      // unmounts BookingsTab and this modal with it, destroying the result
      // above before it can paint. The refresh is deferred to onClose in
      // BookingsTab, which is the same fix 3a3fa24 applied to the bulk slot
      // panel and the one CvScreening.jsx argues for at runScreen.
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to record attendance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h3 className="font-semibold text-gray-800">Confirm Attendance</h3>
        <p className="text-sm text-gray-500 mt-1">
          {booking.candidate_name}
          {booking.slot_date ? ` — ${fmtDateShort(booking.slot_date)}` : ''}
          {booking.slot_time ? ` at ${fmtTime(booking.slot_time)}` : ''}
        </p>

        {done ? (
          <>
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
              <span>
                Recorded as <strong>{done === 'attended' ? 'Attended' : 'No Show'}</strong>.
              </span>
            </div>
            <button onClick={onClose}
              className="mt-4 w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
              Done
            </button>
          </>
        ) : (
          <>
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Remark (optional)
              </label>
              <textarea rows={3} value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Anything worth recording about this interview…"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
            </div>

            {error && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-3 mt-4">
              <button onClick={() => submit('attended')} disabled={saving}
                className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
                <span className="inline-flex items-center justify-center gap-1.5">
                  <CheckCircleIcon className="h-4 w-4" /> Attended
                </span>
              </button>
              <button onClick={() => submit('no_show')} disabled={saving}
                className="flex-1 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-60">
                <span className="inline-flex items-center justify-center gap-1.5">
                  <NoSymbolIcon className="h-4 w-4" /> No Show
                </span>
              </button>
            </div>
            <button onClick={onClose} disabled={saving}
              className="mt-3 w-full py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-60">
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Archived Tab ──────────────────────────────────────────────────────────────
// Past slots that had bookings. The daily cleanup job archives these instead of
// deleting them so the booking record survives; read-only by design.

function ArchivedTab({ slots }) {
  // Most recent first — archived slots are historical, newest is most relevant.
  const grouped = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      if (!map[s.slot_date]) map[s.slot_date] = [];
      map[s.slot_date].push(s);
    });
    return Object.entries(map).sort(([a], [b]) => b.localeCompare(a));
  }, [slots]);

  if (grouped.length === 0) {
    return (
      <EmptyState
        icon={ArchiveBoxIcon}
        title="No archived slots"
        description="Past slots that had bookings are archived here automatically. Past slots with no bookings are removed."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 text-gray-600 text-sm rounded-xl p-3">
        These interview slots have passed and had at least one booking, so they're
        kept here as a record rather than deleted. Their bookings remain visible under
        Past Interviews in <strong>Scheduled Interviews</strong>.
      </div>

      {grouped.map(([date, daySlots]) => (
        <div key={date} className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm opacity-90">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
            <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
            <span className="font-semibold text-gray-600 text-sm">{fmtDateFull(date)}</span>
            <span className="ml-auto text-xs text-gray-400">
              {daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-gray-50">
            {daySlots.map((slot) => (
              <div key={slot.id} className="flex items-center gap-4 px-4 py-3">
                <div className="flex items-center gap-2 w-20">
                  <ClockIcon className="h-4 w-4 text-gray-300" />
                  <span className="text-sm font-medium text-gray-500">{fmtTime(slot.slot_time)}</span>
                </div>

                <span className="text-xs text-gray-500 whitespace-nowrap w-24">
                  {slot.booking_count}/{slot.max_capacity} booked
                </span>

                {slot.location && (
                  <span className="text-xs text-gray-400 truncate max-w-[160px]">{slot.location}</span>
                )}

                {slot.position_title && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {slot.position_title}
                  </span>
                )}

                <div className="ml-auto flex items-center gap-2">
                  <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    <ArchiveBoxIcon className="h-3 w-3" /> Archived
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Template tab — shows the schedule_invite template card ────────────────────

function TemplateTab({ deptId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cvAPI.listEmailTemplates(deptId)
      .then((r) => setTemplates(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [deptId]);

  const scheduleTemplate = templates.find((t) => t.template_type === 'schedule_invite');
  const confirmTemplate = templates.find((t) => t.template_type === 'interview_confirmation');

  const handleSave = async (tpl, updates) => {
    try {
      await cvAPI.updateEmailTemplate(tpl.id, updates);
      const r = await cvAPI.listEmailTemplates(deptId);
      setTemplates(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  if (!scheduleTemplate) return (
    <div className="text-center py-12 text-gray-400">
      Template not found. Try reloading the page.
    </div>
  );

  return (
    <div className="max-w-2xl space-y-8">
      {/* Schedule invite */}
      <div>
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-xl p-3 mb-4">
          <strong>Available placeholders:</strong>{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{candidate_name}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{position_title}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{schedule_link}}'}</code> — the scheduling link is inserted automatically.
        </div>
        <TemplateCard
          template={scheduleTemplate}
          onSave={handleSave}
          title="Schedule Invite Email"
          subtitle="Sent automatically after form submission"
        />
      </div>

      {/* Interview confirmation */}
      {confirmTemplate && (
        <div>
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-xl p-3 mb-4">
            <strong>Available placeholders:</strong>{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{candidate_name}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{position_title}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{date}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{time}}'}</code>,{' '}
            <code className="bg-blue-100 px-1 rounded">{'{{location}}'}</code>
          </div>
          <TemplateCard
            template={confirmTemplate}
            onSave={handleSave}
            title="Interview Confirmed Email"
            subtitle="Sent automatically when a candidate books a slot"
          />
        </div>
      )}
    </div>
  );
}

function TemplateCard({ template, onSave, title = 'Schedule Invite Email', subtitle = 'Sent automatically after form submission' }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body);
  const [active, setActive] = useState(template.active);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(template, { subject, body, active });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div>
          <span className="font-semibold text-gray-800 text-sm">{title}</span>
          <span className="ml-2 text-xs text-gray-400">{subtitle}</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-gray-500">{active ? 'ON' : 'OFF'}</span>
          <div
            onClick={() => { setActive((a) => !a); setEditing(true); }}
            className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${active ? 'bg-primary-500' : 'bg-gray-300'}`}
          >
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${active ? 'translate-x-5' : ''}`} />
          </div>
        </label>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => { setSubject(e.target.value); setEditing(true); }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Body</label>
          <textarea
            rows={8}
            value={body}
            onChange={(e) => { setBody(e.target.value); setEditing(true); }}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        {editing && (
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button onClick={() => {
              setSubject(template.subject); setBody(template.body);
              setActive(template.active); setEditing(false);
            }}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
              Discard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Utility components ────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="text-center py-16 bg-white rounded-xl border border-gray-100 shadow-sm">
      <Icon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
      <h3 className="font-semibold text-gray-700 mb-1">{title}</h3>
      <p className="text-sm text-gray-400">{description}</p>
    </div>
  );
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function fmtTime(t) {
  try {
    const [h, m] = t.split(':').map(Number);
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${period}`;
  } catch {
    return t;
  }
}

function fmtDateFull(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function fmtDateShort(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}
